import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Subscription } from 'rxjs';
import * as fromModel from '../models/';
import { Constant } from '../../../environments/constants';
import { Utils } from '../../../utils/utils';
import { ContextService } from './context.service';
import { PlanService } from '../plan.service';
import { ClientsService } from '../clients.service';
import { AccountsService } from '../accounts.service';
import { ExternalAccountService } from '../external-account.service';
import { GoalAnalysisService } from '../goal-analysis.service';
import { RefDataService } from './ref-data.service';
import { ChartDataService } from './chart-data.service';
import * as fromTransformation from './data-transformation';


export const INIT_APP_DATA: fromModel.IAppData = {
  action: fromModel.DATA_ACTION.NONE,
  actionDispatchedBy: 'Initial',
  actionHistory: [],
  Plan: null,
  PlanAnalysis: null,
  GoalSummary: [],
  GoalSummaryAppliedPlan: null,
  LifeEvents: null,
  //GoalAnalysis: null,
  Clients: null,
  Households: null,
  Accounts: null,
  ExternalAccounts: null
};

@Injectable({
  providedIn: 'root'
})
export class DataService {

  protected appDataSubject: BehaviorSubject<fromModel.IAppData>;
  public appData: fromModel.IAppData; 

  constructor(private contextService: ContextService,
    private planService: PlanService, 
    private clientsService: ClientsService,
    private accountsService: AccountsService,
    private externalAccountService: ExternalAccountService,
    private refDataService: RefDataService,
    private chartDataService: ChartDataService,
    private goalAnalysisService: GoalAnalysisService) { 
      this.appData = JSON.parse(JSON.stringify(INIT_APP_DATA));
      this.appDataSubject = new BehaviorSubject<fromModel.IAppData>(this.appData);
  }

  public subscribe(callback: (context: fromModel.IAppData) => void): Subscription{
    return this.appDataSubject.subscribe(callback);    
  }

  public dispatchAction(act: fromModel.DATA_ACTION, dispatchedBy: string, payload: any | null): void {
    console.log(`%c App Data - Action: ${act}; By: ${dispatchedBy}`, Constant.consoleLogStyle.documentContext);
    let _contextUpdateReq = true;
    
    this.appData.action = act;
    this.appData.actionDispatchedBy = dispatchedBy;
    this.appData.actionHistory.push(`${act}: ${dispatchedBy}`);
    
    switch (act){
      case fromModel.DATA_ACTION.NONE:
        console.log('%c App Context - No Action', Constant.consoleLogStyle.documentContext);
        break;
      case fromModel.DATA_ACTION.RESET:  
        console.log('%c Reset App Context', Constant.consoleLogStyle.documentContext);
        this.appData = JSON.parse(JSON.stringify(INIT_APP_DATA));      
        break;
      case fromModel.DATA_ACTION.RESETRESULTS:
        this.resetResults();
        break;
      case fromModel.DATA_ACTION.CLIENTS: 
        

        /* Upatding Client Name in the plan based on client details*/
        if(this.appData.Plan && this.appData.Plan.planClients && this.appData.Plan.planClients.length>0
          && payload.Clients && payload.Clients['value'] && payload.Clients['value'].length>0){
            this.appData.Plan.planClients.forEach(pCl => {
              const cl = payload.Clients['value'].find(_cl=>_cl.LegalEntityID.trim() === pCl.clientid);
              if(cl){
                pCl.clientname = cl.LegalEntityName;
                cl.dateofBirth = pCl.dob;                
                cl.role = pCl.role;
                pCl.legalEntityType = cl.legalEntityType;
                // cl.gender = '';
                // cl.retAge = '-';
                // cl.endAge = '-';
                // cl.retAge = '-';
              } 
            });
        }
        if(payload.Clients['value'] && payload.Clients['value'].length>0) {
          payload.Clients['value'].forEach(cl => {
            if(cl && cl.LegalEntityName){
              // let clFirstLastName = cl.LegalEntityName.trim().split(' ');
              // if(clFirstLastName && clFirstLastName.length>0){
              //   let initials='';
              //   clFirstLastName.forEach(name => {
              //     initials = initials + name.substr(0,1).toUpperCase();
              //   });
              //   cl.initials = initials.trim();                
              // }
              cl.initials = this.getClientInitials(cl.LegalEntityName);
            }
            cl.clientname =  cl.LegalEntityName;
            cl.dob = cl.dateofBirth;
            cl.age =  Utils.calculateAge(cl.dateofBirth);
            if(!cl.role){
              cl.role = fromModel.CLIENTROLE.Other;
            }
          });
        }     

        if(payload.Error){
          this.appData.Clients = payload;
          this.appData.Households = payload;
        } else {
          this.appData.Clients = payload.Clients; 
          this.appData.Households = payload.Households;
        }        
        break;
      case fromModel.DATA_ACTION.UPDATECLIENT: 
        let pClient = this.appData.Plan.planClients.find(pCl=>pCl.clientid === payload.planClient.clientid);
        if(pClient){
          // let elePos = this.appData.Plan.planClients.indexOf(pClient);
          // this.appData.Plan.planClients.splice(elePos,1);
          pClient.dob = payload.planClient.dob;
          pClient.age = payload.planClient.age;      
          pClient.gender = payload.planClient.gender
          pClient.stateOfResi = payload.planClient.stateOfResi
          pClient.empType = payload.planClient.empType
          pClient.salary = payload.planClient.salary
        } else {
          this.appData.Plan.planClients.push(payload.planClient);
        }
        
        break;
      case fromModel.DATA_ACTION.ACCOUNTS: 
        this.appData.Accounts = payload;
        break;
      case fromModel.DATA_ACTION.EXTERNALACCOUNTS: 
        this.appData.ExternalAccounts = payload;
        break;
      case fromModel.DATA_ACTION.INFLIGHTPLAN:
      case fromModel.DATA_ACTION.PLAN:
        if(payload && payload.planClients && payload.planClients.length>0) {
          payload.planClients.forEach(cl => {
            if(cl && cl.clientname){
              let clFirstLastName = cl.clientname.trim().split(' ');
              if(clFirstLastName && clFirstLastName.length>0){
                let initials='';
                clFirstLastName.forEach(name => {
                  initials = initials + name.substr(0,1).toUpperCase();
                });
                cl.initials = initials.trim();                
              }
            }
            cl.age =  Utils.calculateAge(cl.dob);
          });
        }        
        this.appData.Plan = payload;        
        break;
      case fromModel.DATA_ACTION.CREATEGOAL:
        this.createGoal(payload.goal);
        this.updateExpense(payload.expense);
        this.appData.PlanAnalysis = null;
        this.appData.GoalSummary = [];
        break;        
      case fromModel.DATA_ACTION.PLANANALYSIS:
        this.appData.PlanAnalysis = payload;   
        break;
      case fromModel.DATA_ACTION.GOALSUMMARY:
        this.addGoalSummary(payload);
        this.addLifeEvents(payload);
        this.addGoalSummaryAppliedPlan(payload);
        break;      
      case fromModel.DATA_ACTION.SELECTEDPLANCLIENT:
        const pclient = this.appData.Plan.planClients.find(plClient=>plClient.clientid === payload.clientid);
        if(pclient){
          //fromTransformation.ClientDataTransformaion.removePlanClient(payload.clientid, this, this.contextService);
          //fromTransformation.ClientDataTransformaion.removeClient(payload.clientid, this, this.clientsService);
        } else {
          let client = this.getClientByClientId(payload.clientid);
          if(client){
            let selectedClient: fromModel.PlanClient = {
              clientid: client.LegalEntityID,
              clientname: client.LegalEntityName,
              initials: this.getClientInitials(client.LegalEntityName),
              role: fromModel.CLIENTROLE.Other,
              dob: client.dateofBirth,
              age: Utils.calculateAge(client.dateofBirth),
              legalEntityType: client.legalEntityType,
              // endAge?: number;
              // gender?: string;
              // stateOfResi?: string;                  
              // retAge?: number;
              // salary?: number;
              // empType?: EMPLOYMENTTYPE;
              isIncluded: true
            };
            this.appData.Plan.planClients.push(selectedClient);
          }
        }
        break;
      case fromModel.DATA_ACTION.SELECTEDPLANACCOUNTS:        
        this.appData.Plan.planAccounts = [];
        payload.selectedAccounts.forEach(acc => {
          this.appData.Plan.planAccounts.push({actNo: acc, isIncluded: true});
        }); 
        payload.selectedEAAccounts.forEach(acc => {
          this.appData.Plan.planAccounts.push({actNo: acc, custodian: fromModel.CUSTODIANTYPE.HypoEA, isIncluded: true});
        }); 
        this.appData.PlanAnalysis = null;
        this.appData.GoalSummary = [];                   
        break;
      case fromModel.DATA_ACTION.SELECTEDPLANLIABILITIES:        
        this.appData.Plan.planLiabilities = [];
        payload.selectedLiabilities.forEach(acc => {
          this.appData.Plan.planLiabilities.push({actNo: acc, isIncluded: true});
        }); 
        payload.selectedEALiabilities.forEach(acc => {
          this.appData.Plan.planLiabilities.push({actNo: acc, custodian: fromModel.CUSTODIANTYPE.HypoEA, isIncluded: true});
        }); 
        // this.appData.PlanAnalysis = null;
        // this.appData.GoalSummary = [];                   
        break;
      case fromModel.DATA_ACTION.SELECTEDEXPENSES:   
        if(payload && !payload.goalId){   
          if(this.appData.Plan.expenses && this.appData.Plan.expenses.length>0){
            this.appData.Plan.expenses.forEach(exp => {
              const _expId = payload.expenses.find(expId=>expId === exp.expId);
              if(_expId){
                exp.isIncluded = true;
              } else {
                exp.isIncluded = false;
              }
            });
            this.appData.PlanAnalysis = null;
            this.appData.GoalSummary = [];
          }
        } else {
          let exps = this.getAllExpensesByGoalId(payload.goalId);
          exps.forEach(exp => {
            const _expId = payload.expenses.find(expId=>expId === exp.expId);
            if(_expId){
              exp.isIncluded = true;
            } else { 
              exp.isIncluded = false;
            }
          });
          this.appData.PlanAnalysis = null;
          this.appData.GoalSummary = [];
          //this.removeGoalSummaryForGoalId(payload.goalId);
        }
        break;
      case fromModel.DATA_ACTION.ADDEXPENSE:
        this.updateExpense(payload.expense);
        this.updateGoalExpense(payload.goalId, payload.expense.expId);
        this.appData.PlanAnalysis = null;
        if(payload && payload.goalId){            
          this.appData.GoalSummary = [];
          //this.removeGoalSummaryForGoalId(payload.goalId);
        } else {
          this.appData.GoalSummary = [];
        }
        break; 
      case fromModel.DATA_ACTION.UPDATEEXPENSE:
        this.updateExpense(payload.expense);
        this.appData.PlanAnalysis = null;
        if(payload && payload.goalId){            
          //this.removeGoalSummaryForGoalId(payload.goalId);
          this.appData.GoalSummary = [];
        } else {
          this.appData.GoalSummary = [];
        }
        break; 
      case fromModel.DATA_ACTION.DELETEEXPENSE:
        this.updateExpense(payload.expense, true);
        this.updateGoalExpense(payload.goalId, payload.expense.expId, true);
        this.appData.PlanAnalysis = null;
        if(payload && payload.goalId){                      
          this.appData.GoalSummary = [];
        } else {
          this.appData.GoalSummary = [];
        }
        break;
      case fromModel.DATA_ACTION.SELECTEDINCOME:        
        if(this.appData.Plan.incomes && this.appData.Plan.incomes.length>0){
          this.appData.Plan.incomes.forEach(inc => {
            const _incId = payload.find(incId=>incId === inc.incId);
            if(_incId){
              inc.isIncluded = true;
            } else {
              inc.isIncluded = false;
            }
          });
          this.appData.PlanAnalysis = null;
          this.appData.GoalSummary = [];        
        }
        break;
      case fromModel.DATA_ACTION.ADDINCOME:
        this.updateIncome(payload.income);
        this.appData.PlanAnalysis = null;
        this.appData.GoalSummary = [];        
        break; 
      case fromModel.DATA_ACTION.UPDATEINCOME:
        this.updateIncome(payload.income);
        this.appData.PlanAnalysis = null;
        this.appData.GoalSummary = [];
        break;        
      case fromModel.DATA_ACTION.DELETEINCOME:
        this.updateIncome(payload.income, true);
        this.appData.PlanAnalysis = null;
        this.appData.GoalSummary = [];        
        break;
      case fromModel.DATA_ACTION.ADDSAVING:
        payload.forEach(item => {
          this.updateSaving(item.saving);  
        }); 
        this.appData.PlanAnalysis = null;
        this.appData.GoalSummary = [];        
        break; 
      case fromModel.DATA_ACTION.UPDATESAVING:
        payload.forEach(item => {
          this.updateSaving(item.saving);  
        });        
        this.appData.PlanAnalysis = null;
        this.appData.GoalSummary = [];
        break;    
      case fromModel.DATA_ACTION.PLANIDNAME:
        //_contextUpdateReq = false;
        if(payload.Error){
          this.appData.Plan.planSaveError = true;
        } else {
          this.appData.Plan.id = payload.id;
          this.appData.Plan.planName = payload.planName;
          this.appData.Plan.planSaveError = false;
        }
        break;      
      case fromModel.DATA_ACTION.UPDATEPLANRESULTVIEWBY:
        this.appData.Plan.planResultViewBy = payload;
        break;        
      case fromModel.DATA_ACTION.UPDATESETTINGS:
        this.appData.Plan.settings = payload;
        this.resetResults();        
        break;
    }
      
    // console.log(JSON.stringify(this.context));  
    if(_contextUpdateReq){
      const dispatchedContext: fromModel.IAppData = JSON.parse(JSON.stringify(this.appData));    
      this.appDataSubject.next(dispatchedContext);
    }
  }

  resetResults(){
      this.appData.PlanAnalysis = null;
      this.appData.GoalSummary = [];   
      this.appData.LifeEvents  = null;
      this.appData.GoalSummaryAppliedPlan  = null;
  }

  removeClient(clientid){

    fromTransformation.ClientDataTransformaion.removeClient(clientid, this, this.clientsService);
    
    // remove income from plan
    // remove income ids from goals

    // remove expense from plan
    // remove expense ids from goals

    //Check goal needs to be deleted?    
    
    /******** START: remove accounts from plan ********/
    // get all internal account  ids  from client object array by client id
    // then remove accounts from Plan Goals
    // then remove accounts from Plan Accounts
    // remove savings associated with all these accounts from Saving object
    // remove savings associated with all these accounts from goal- savings object
    
    // get all ExternalAccounts  ids  from ExternalAccounts object array by client id
    // then remove accounts from Plan Accounts
    // then remove accounts from Plan Goals
    // remove savings associated with all these accounts from Saving object
    // remove savings associated with all these accounts from goal- savings object
    /******** END: remove accounts from plan ********/
    
    // remove client from plan clients

    /******** Start: Nullyfy results ********/
    // PlanAnalysis
    // GoalSummary
    // GoalSummaryAppliedPlan
    // LifeEvents
    //call setClientInContext()
      // Accounts
      // Clients
      //ExternalAccounts
    /******** End: Nullyfy results ********/
    


  }

  /* Context Related functions*/
  createGoal(payload){
    if(this.appData.Plan.goals){
      this.appData.Plan.goals.push(payload);
    }
    this.getGoalSummaryByGoalId(payload.goalId);
  }

  updateGoalExpense(goalId: string, expId: string, del?: boolean){
    if(this.appData.Plan.goals){
      let goal = this.appData.Plan.goals.find(goal=>goal.goalId === goalId);
      const goalPos = this.appData.Plan.goals.indexOf(goal);
      if(goal && goalPos>-1){        
        if(del){ //delete expense 
          const _exp = this.appData.Plan.goals[goalPos].expenses.find(_expId=> _expId === expId);
          if(_exp){
            const _expPosition = this.appData.Plan.goals[goalPos].expenses.indexOf(_exp);
            if(_expPosition > -1){
              this.appData.Plan.goals[goalPos].expenses.splice(_expPosition, 1);
              if(this.appData.Plan.goals[goalPos].expenses.length === 0){
                this.deleteGoal(goalId);
              }
            }
          }          
        } else { //update expense          
          this.appData.Plan.goals[goalPos].expenses.push(expId);
        }
      }
    }
  }

  deleteGoal(goalId){
    if(this.appData.Plan.goals && this.appData.Plan.goals.length>0){
      const _goal = this.appData.Plan.goals.find(goal=> goal.goalId === goalId);
      if(_goal){
        const _goalPosition = this.appData.Plan.goals.indexOf(_goal);
        if(_goalPosition > -1){
          this.appData.Plan.goals.splice(_goalPosition, 1);
        }
      }  
    }
  }

  updateExpense(payload,del?: boolean){
    if(this.appData.Plan.expenses){
      const _exp = this.appData.Plan.expenses.find(exp=> exp.expId === payload.expId);
      if(_exp){
        const _expPosition = this.appData.Plan.expenses.indexOf(_exp);
        if(_expPosition > -1){
          if(del){ //delete expense 
            this.appData.Plan.expenses.splice(_expPosition, 1);
          } else { //update expense          
            this.appData.Plan.expenses[_expPosition] = payload;            
          }
        }
      } else { //add expense
        this.appData.Plan.expenses.push(payload);
      }    
    }
  }

  updateIncome(payload,del?: boolean){
    if(this.appData.Plan.incomes && this.appData.Plan.incomes.length>0){
      const _inc = this.appData.Plan.incomes.find(inc=> inc.incId === payload.incId);
      if(_inc){
        const _incPosition = this.appData.Plan.incomes.indexOf(_inc);
        if(_incPosition > -1){
          if(del){ //delete income 
            this.appData.Plan.incomes.splice(_incPosition, 1);
          } else { //update expense          
            this.appData.Plan.incomes[_incPosition] = payload;            
          }
        }
      } else { //add income
        this.appData.Plan.incomes.push(payload);
      }    
    }
  }

  updateSaving(payload,del?: boolean){
    if(this.appData.Plan && !this.appData.Plan.savings){
      this.appData.Plan.savings = [];
    }
    const _sav = this.appData.Plan.savings.find(sav=> sav.savId === payload.savId);
    if(_sav){
      const _savPosition = this.appData.Plan.savings.indexOf(_sav);
      if(_savPosition > -1){
        if(del){ //delete saving 
          this.appData.Plan.savings.splice(_savPosition, 1);
        } else { //update saving          
          this.appData.Plan.savings[_savPosition] = payload;            
        }
      }
    } else { //add saving
      this.appData.Plan.savings.push(payload);
    }
  }


  removeGoalSummaryForGoalId(goalId: string){
    if(this.appData.GoalSummary && this.appData.GoalSummary.length>0){
      const goalSum = this.appData.GoalSummary.find(_g=>_g.goalId === goalId);
      const goalSumPos = this.appData.GoalSummary.indexOf(goalSum);
      if(goalSumPos > -1){
        this.appData.GoalSummary.splice(goalSumPos, 1);              
      } 
      //this.getGoalSummaryByGoalId(goalId);
    }
  }

  addGoalSummary(payload){
    if(payload && payload.goalSmry && payload.goalSmry.length>0){
      this.appData.GoalSummary = [];
      payload.goalSmry.forEach(gsam => {
        let _goal = this.getGoalByGoalId(gsam.goalId);
        let _color;
        if(_goal){
          _color = this.refDataService.getPlanningUIRefDataByTypeCode(fromModel.PLANNINGUIREFTYPE.GoalsColor, _goal.goalType).value;
          this.appData.GoalSummary.push(
              {goalId: _goal.goalId, 
              goalType: _goal.goalType,
              goalDesc: _goal.goalDesc,
              priority: _goal.priority,
              color: _color,
              GoalSummary: gsam}
            );
          }
      });
    } else {
      this.appData.GoalSummary = payload;
    }
  }

  addGoalSummaryAppliedPlan(payload){
    if(payload && payload.appliedPlan){
      this.appData.GoalSummaryAppliedPlan = payload.appliedPlan;
    } else {
      this.appData.GoalSummaryAppliedPlan = payload;
    }
  }

  addLifeEvents(payload){
    if(payload && payload.impLifeEvents && payload.impLifeEvents.length>0){
      this.appData.LifeEvents = [];
      let _color;
      this.appData.LifeEvents = payload.impLifeEvents;
      this.appData.LifeEvents.forEach(lifeEvent => {
      let _client = this.getPlanClientByClientId(lifeEvent.clientId);        
        if(_client){
          _color = this.refDataService.getPlanningUIRefDataByTypeCode(fromModel.PLANNINGUIREFTYPE.ClientsColor, _client.role).value;
          lifeEvent.clientname = _client.clientname;
          lifeEvent.age = _client.age;
          lifeEvent.role = _client.role;
          lifeEvent.initials = _client.initials;
          lifeEvent.color = _color;         
          }
      });
      
    } else {
      this.appData.LifeEvents = payload;
    }
  }
  
  /* Clients */
  public getClientInitials(clientName){
    let initials: string = "";
    if(clientName){
      let clFirstLastName = clientName.trim().split(' ');
      if(clFirstLastName && clFirstLastName.length>0){
        let int='';
        clFirstLastName.forEach(name => {
          int = int + name.substr(0,1).toUpperCase();
        });
        initials = int.trim();                
      }
    }
    return initials;
  }

  
  public getClients(clientIds){
    //this.clientsService.getClients(clientIds, this);
  }

  public getAllClients(): any[] {
    let clients:  any[];
    if(this.appData && this.appData.Clients && this.appData.Clients.value && this.appData.Clients.value.length>0){
      clients = this.appData.Clients.value;       
    }
    return clients;
  }


  public getPlanClients(): fromModel.PlanClient[] {
    let clients:  fromModel.PlanClient[];
    if(this.appData && this.appData.Plan && this.appData.Plan.planClients && this.appData.Plan.planClients.length>0){
      clients = this.appData.Plan.planClients;       
    }
    return clients;
  }

  public getAllClientIds(): string[] {
    let clientIds:  string[];
    if(this.appData && this.appData.Plan && this.appData.Plan.planClients && this.appData.Plan.planClients.length>0){
      clientIds = this.appData.Plan.planClients.map(cl => cl.clientid);
    }
    return clientIds;
  }

  public getClientByRole(clRole: fromModel.CLIENTROLE): fromModel.PlanClient {
    let client:  fromModel.PlanClient;
    if(this.appData && this.appData.Plan && this.appData.Plan.planClients && this.appData.Plan.planClients.length>0){
      const _cl = this.appData.Plan.planClients.find(cl=> cl.role === clRole);
      if(_cl){
        client = _cl;
      }
    }
    return client;
  }

  public getClientByClientId(clientId: string): any {
    let client:  any;
    if(this.appData && this.appData.Clients && this.appData.Clients.value && this.appData.Clients.value.length>0){
      const _cl = this.appData.Clients.value.find(cl=> cl.LegalEntityID === clientId);
      if(_cl){
        client = _cl;
      }
    }
    return client;
  }

  public getPlanClientByClientId(clientId: string): fromModel.PlanClient {
    let client:  fromModel.PlanClient;
    if(this.appData && this.appData.Plan && this.appData.Plan.planClients && this.appData.Plan.planClients.length>0){
      const _cl = this.appData.Plan.planClients.find(cl=> cl.clientid === clientId);
      if(_cl){
        client = _cl;
      }
    }
    return client;
  }

  public getPlanOwners(): fromModel.PlanClient[] {
    let clients: fromModel.PlanClient[] = [];
    let _cl: fromModel.PlanClient;
    if(this.appData && this.appData.Plan && this.appData.Plan.planClients && this.appData.Plan.planClients.length>0){
      _cl = this.appData.Plan.planClients.find(cl=> cl.role === fromModel.CLIENTROLE.Primary);
      if(_cl){
        clients.push(_cl);
      }
      _cl = this.appData.Plan.planClients.find(cl=> cl.role === fromModel.CLIENTROLE.Secondary);
      if(_cl){
        clients.push(_cl);
      }
      if(clients.length === 2){
        _cl = {
          clientid: "0",
          clientname: "Joint",
          role: fromModel.CLIENTROLE.Joint
        };
        clients.push(_cl);
      }
    }
    return clients;
  }

  public getPlanPrimarSecondaryOwners(): fromModel.PlanClient[] {
    let clients: fromModel.PlanClient[] = [];
    let _cl: fromModel.PlanClient;
    if(this.appData && this.appData.Plan && this.appData.Plan.planClients && this.appData.Plan.planClients.length>0){
      _cl = this.appData.Plan.planClients.find(cl=> cl.role === fromModel.CLIENTROLE.Primary);
      if(_cl){
        clients.push(_cl);
      }
      _cl = this.appData.Plan.planClients.find(cl=> cl.role === fromModel.CLIENTROLE.Secondary);
      if(_cl){
        clients.push(_cl);
      }
    }
    return clients;
  }

  public isSecondaryClient(){
      let isSClient = false;
      if(this.getClientByRole(fromModel.CLIENTROLE.Secondary)){
        isSClient = true;
      }
      return isSClient;
  }

  getClientNameAge(yr): any{
    let pName;  
    let pAge;
    let sName;
    let sAge;
    
    if(this.getClientByRole(fromModel.CLIENTROLE.Primary)){
      pName = this.getClientByRole(fromModel.CLIENTROLE.Primary).clientname;
      pAge = Utils.calculateAgeForYear(this.getClientByRole(fromModel.CLIENTROLE.Primary).dob, yr);
    }
    if(this.isSecondaryClient()){
      sName = this.getClientByRole(fromModel.CLIENTROLE.Secondary).clientname;          
      sAge = Utils.calculateAgeForYear(this.getClientByRole(fromModel.CLIENTROLE.Secondary).dob, yr);        
    }
    return {
      pName: pName,
      pAge: pAge,
      sAge: sAge,
      sName: sName
    };
}

  /* Accounts */
  public getAccounts(accountNumbers){
    this.accountsService.getAccounts(accountNumbers, this);
  }

  public getAllAccounts(): fromModel.PlanAccount[] {
    let accounts:  fromModel.PlanAccount[];
    if(this.appData && this.appData.Plan && this.appData.Plan.planAccounts && this.appData.Plan.planAccounts.length>0){
      accounts = this.appData.Plan.planAccounts;       
    }
    return accounts;
  }
  
  public getAllIncludedAccounts(): fromModel.PlanAccount[] {
    let accounts:  fromModel.PlanAccount[];
    if(this.appData && this.appData.Plan && this.appData.Plan.planAccounts && this.appData.Plan.planAccounts.length>0){
      accounts = this.appData.Plan.planAccounts.filter(({isIncluded}) => isIncluded === true);
    }
    return accounts;
  }

  public getAllExcludedAccounts(): fromModel.PlanAccount[] {
    let accounts:  fromModel.PlanAccount[];
    if(this.appData && this.appData.Plan && this.appData.Plan.planAccounts && this.appData.Plan.planAccounts.length>0){
      accounts = this.appData.Plan.planAccounts.filter(({isIncluded}) => isIncluded === false);
    }
    return accounts;
  }

  public getAllAccountDetails(): any[] {
    let accounts: any[] = [];
    if(this.appData.Accounts && this.appData.Accounts['value'] && this.appData.Accounts['value'].length>0){
      this.appData.Accounts['value'].forEach(acc => {
        if(acc.taxstatus){
            acc.taxStatusColor = this.refDataService.getPlanningUIRefDataByTypeCode(fromModel.PLANNINGUIREFTYPE.Colors,acc.taxstatus.replace('-','').toUpperCase()).value;
        }
        accounts.push(acc);
      }); 
    }
    return accounts;
  }

  public getAllExternalAccountDetails(): any[] {
    let extAccounts: any[] = [];
    if(this.appData.ExternalAccounts && this.appData.ExternalAccounts['accounts'] && this.appData.ExternalAccounts['accounts'].length>0){
      this.appData.ExternalAccounts['accounts'].forEach(acc => {
        if(acc.taxstatus){
          acc.taxStatusColor = this.refDataService.getPlanningUIRefDataByTypeCode(fromModel.PLANNINGUIREFTYPE.Colors,acc.taxstatus.replace('-','').toUpperCase()).value;
        }
        extAccounts.push(acc);
      }); 
    }
    return extAccounts;
  }

  public getPlanAccountDetails(): any[] {
    let accounts: any[] = [];
    let externalAccounts: any[] = [];
    let planAccounts: any[] = [];    
    accounts = this.getAllAccountDetails();
    if(accounts && accounts.length>0){
      accounts.forEach(acc => {
        const pAcc = this.appData.Plan.planAccounts.find(_pac=>_pac.actNo === acc.id);
        if(pAcc){
          acc.isIncluded = true;            
        } else {
          acc.isIncluded = false;
        }
        planAccounts.push(acc);
      }); 
    }
    externalAccounts = this.getAllExternalAccountDetails();
    if(externalAccounts && externalAccounts.length>0){
      externalAccounts.forEach(exacc => {
        const pAcc = this.appData.Plan.planAccounts.find(_pac=>_pac.actNo === exacc.id);
        if(pAcc){
          exacc.isIncluded = true;            
        } else {
          exacc.isIncluded = false;
        }
        exacc.CustodianType = fromModel.CUSTODIANTYPE.HypoEA;
        planAccounts.push(exacc);
      }); 
    }
    return planAccounts;
  }

  public getPlanAccountDetailsByAccId(accId: string): any {
    let accounts: any[] = [];
    let planAccount: any;
    accounts = this.getPlanAccountDetails();
    if(accounts && accounts.length>0){      
      planAccount = accounts.find(_pac=>_pac.id === accId);              
    }
    return planAccount;
  }

  public getPlanAccountDetailsByTaxStatus(taxStatus: fromModel.TAXSTATUS): any[] {
    let accounts: any[];
    let externalAccounts: any[];
    let filtertedAccounts: any[];
    accounts = this.getPlanAccountDetails();
    if(accounts && accounts.length>0){
      const ac = accounts.filter(({taxstatus, isIncluded})=>taxstatus && taxstatus.toUpperCase() === taxStatus && isIncluded);
      if(ac && ac.length>0){
        filtertedAccounts = ac;
      }
    }
    return filtertedAccounts;
  }

  public getPlanAccountTotalByTaxStatus(taxStatus: fromModel.TAXSTATUS): number {
    let accountTotal: number = 0; 
    let accounts: any[];
    accounts = this.getPlanAccountDetailsByTaxStatus(taxStatus);
    if(accounts && accounts.length>0){
      accounts.forEach(ac => {
        if(ac){
          if(ac.CustodianType === fromModel.CUSTODIANTYPE.HypoEA){
            accountTotal += ac.mktValue;
          } else {
            accountTotal += ac.AccountKPIs.AccountTotal;
          }
        }
        
      });
    }
    return accountTotal;
  }

  /* External Accounts (Hypothetical) */
  public getExternalAccountListByClientIds(){
    let clientIds = this.getAllClientIds(); 
    if(clientIds && clientIds.length>0){
      this.externalAccountService.getExternalAccountListByClientIds(clientIds, this);
    }
  }


  /* Savings */

/* Savings */
public getAllSavings(): fromModel.Saving[] {
  let savings:  fromModel.Saving[];
  if(this.appData && this.appData.Plan && this.appData.Plan.settings && this.appData.Plan.settings.length>0){
    savings = this.appData.Plan.savings;      
  }
  return savings;
}

public getAllIncludedSavings(): fromModel.Saving[] {
  let savings:  fromModel.Saving[];
  if(this.appData && this.appData.Plan && this.appData.Plan.savings && this.appData.Plan.savings.length>0){
    savings = this.appData.Plan.savings.filter(({isIncluded}) => isIncluded === true);   
  }
  return savings;
}

public getAllExcludedSavings(): fromModel.Saving[] {
  let savings:  fromModel.Saving[];
  if(this.appData && this.appData.Plan && this.appData.Plan.savings && this.appData.Plan.savings.length>0){
    savings = this.appData.Plan.savings.filter(({isIncluded}) => isIncluded === false);   
  }
  return savings;
}

public getSavingByType(savType: fromModel.SAVINGSTYPE): fromModel.Saving[] {
  let savings:  fromModel.Saving[];
  if(this.appData && this.appData.Plan && this.appData.Plan.savings && this.appData.Plan.savings.length>0){
    savings = this.appData.Plan.savings.filter(({savType}) => savType === savType);     
  }
  return savings;
}

public getSavingById(savId: string): fromModel.Saving {
  let saving:  fromModel.Saving;
  if(this.appData && this.appData.Plan && this.appData.Plan.savings && this.appData.Plan.savings.length>0){
    const _sav = this.appData.Plan.savings.find(sav=> sav.savId === savId);
    if(_sav){
      saving = _sav;
    }      
  }
  return saving;
}

public getSavingsByAccId(accId: string): fromModel.Saving[] {
  let savings:  fromModel.Saving[];
  if(this.appData && this.appData.Plan && this.appData.Plan.savings && this.appData.Plan.savings.length>0){
    savings = this.appData.Plan.savings.filter(({actId}) => actId === accId);       
  }
  return savings;
}

public getSavingsByAccIdType(accId: string, sType: fromModel.SAVINGSTYPE): fromModel.Saving {
  let saving:  fromModel.Saving;
  if(this.appData && this.appData.Plan && this.appData.Plan.savings && this.appData.Plan.savings.length>0){
    saving = this.appData.Plan.savings.find(saving => saving.actId === accId && saving.savType === sType);       
  }
  return saving;
}

public getContributionByTypeAccId(sType: fromModel.SAVINGSTYPE,  accId: string): number {
  let conAmountTotal: number = 0;
  if(this.appData && this.appData.Plan && this.appData.Plan.savings && this.appData.Plan.savings.length>0){
    const savings = this.appData.Plan.savings.filter(({savType, actId}) => savType === sType && actId === accId);       
    if(savings && savings.length>0){
      savings.forEach(sav => {
        if(sav.amtPeriod && sav.amtPeriod.length>0){
          sav.amtPeriod.forEach(amtPer => {
            conAmountTotal +=  Number(amtPer.amount);            
          });
        }        
      });
    }
  }  
  return conAmountTotal;
}

  /* Expenses */
  public getAllExpenses(): fromModel.Expense[] {
    let expenses:  fromModel.Expense[];
    if(this.appData && this.appData.Plan && this.appData.Plan.expenses && this.appData.Plan.expenses.length>0){
      expenses = this.appData.Plan.expenses;      
    }
    return expenses;
  }

  public getAllExpensesByGoalType(goalType: fromModel.GOALTYPE): fromModel.Expense[] {
    let expenses:  fromModel.Expense[];
    let goal: fromModel.Goal = this.getGoalByType(goalType);
    if(goal && goal.expenses && goal.expenses.length>0){
      expenses = this.getAllExpenses().filter(({ expId })=> goal.expenses.includes(expId));
    }   
    
    return expenses;
  }

  public getAllExpensesByGoalId(goalId: string): fromModel.Expense[] {
    let expenses:  fromModel.Expense[];
    let goal: fromModel.Goal = this.getGoalByGoalId(goalId);
    if(goal && goal.expenses && goal.expenses.length>0){
      expenses = this.getAllExpenses().filter(({ expId })=> goal.expenses.includes(expId));
    }   
    
    return expenses;
  }

  public getAllIncludedExpenses(): fromModel.Expense[] {
    let expenses:  fromModel.Expense[];
    if(this.appData && this.appData.Plan && this.appData.Plan.expenses && this.appData.Plan.expenses.length>0){
      expenses = this.appData.Plan.expenses.filter(({isIncluded}) => isIncluded === true);      
    }
    return expenses;
  }

  public getAllExcludedExpenses(): fromModel.Expense[] {
    let expenses:  fromModel.Expense[];
    if(this.appData && this.appData.Plan && this.appData.Plan.expenses && this.appData.Plan.expenses.length>0){
      expenses = this.appData.Plan.expenses.filter(({isIncluded}) => isIncluded === false);      
    }
    return expenses;
  }

  public getExpensesByType(expType: fromModel.EXPENSETYPE): fromModel.Expense[] {
    let expense:  fromModel.Expense[];
    if(this.appData && this.appData.Plan && this.appData.Plan.expenses && this.appData.Plan.expenses.length>0){
      const _exp = this.appData.Plan.expenses.filter(exp=> exp.expType === expType);
      if(_exp){
        expense = _exp;
      }      
    }
    return expense;
  }
     
  public getExpenseByExpenseId(expId: string): fromModel.Expense {
    let expense:  fromModel.Expense;
    if(this.appData && this.appData.Plan && this.appData.Plan.expenses && this.appData.Plan.expenses.length>0){
      const _exp = this.appData.Plan.expenses.find(exp=> exp.expId === expId);
      if(_exp){
        expense = _exp;
      }      
    }
    return expense;
  } 

  /* Incomes */
  public getAllIncomes(): fromModel.Income[] {
    let incomes:  fromModel.Income[];
    if(this.appData && this.appData.Plan && this.appData.Plan.incomes && this.appData.Plan.incomes.length>0){
      incomes = this.appData.Plan.incomes;      
    }
    return incomes;
  }

  public getAllIncludedIncomes(): fromModel.Income[] {
    let incomes:  fromModel.Income[];
    if(this.appData && this.appData.Plan && this.appData.Plan.incomes && this.appData.Plan.incomes.length>0){
      incomes = this.appData.Plan.incomes.filter(({isIncluded}) => isIncluded === true);   
    }
    return incomes;
  }

  public getAllExcludedIncomes(): fromModel.Income[] {
    let incomes:  fromModel.Income[];
    if(this.appData && this.appData.Plan && this.appData.Plan.incomes && this.appData.Plan.incomes.length>0){
      incomes = this.appData.Plan.incomes.filter(({isIncluded}) => isIncluded === false);
    }
    return incomes;
  }

  public getIncomesByType(incType: fromModel.INCOMETYPE): fromModel.Income[] {
    let income:  fromModel.Income[];
    if(this.appData && this.appData.Plan && this.appData.Plan.incomes && this.appData.Plan.incomes.length>0){
      const _inc = this.appData.Plan.incomes.filter(inc=> inc.incType === incType);
      if(_inc){
        income = _inc;
      }      
    }
    return income;
  }

  public getIncomeByIncomeId(incId: string): fromModel.Income {
    let income:  fromModel.Income;
    if(this.appData && this.appData.Plan && this.appData.Plan.incomes && this.appData.Plan.incomes.length>0){
      const _inc = this.appData.Plan.incomes.find(inc=> inc.incId === incId);
      if(_inc){
        income = _inc;
      }      
    }
    return income;
  }  

  public getAllIncomeAmount(): number{
    let incomeTotal: number = 0;
    let incomes = this.getAllIncomes();
    if(incomes && incomes.length>0){
      incomes.forEach(inc => {
        if(inc.amtPeriod && inc.amtPeriod.length>0){
          inc.amtPeriod.forEach(amtPer => {
            incomeTotal +=  amtPer.amount;  
          });
        }
      });
    }
    return incomeTotal;
  }

  public getIncomeAmountByType(incType: fromModel.INCOMETYPE, role: fromModel.CLIENTROLE): number{
    let incomeTotal: number = 0;
    let incomes = this.getIncomesByType(incType);
    if(incomes && incomes.length>0){
      incomes.forEach(inc => {
        if(inc.isIncluded){
          const client = this.getPlanClientByClientId(inc.owner);
          if(client && client.role === role){
            if(inc.amtPeriod && inc.amtPeriod.length>0){
              inc.amtPeriod.forEach(amtPer => {
                incomeTotal +=  amtPer.amount;  
              });
            }
          }
        }
      });
    }
    return incomeTotal;
  }

  public getEmpIncomeAmountByRole(role: fromModel.CLIENTROLE): number{
    let incomeTotal: number = 0;
    incomeTotal += this.getIncomeAmountByType(fromModel.INCOMETYPE.EI, role);
    incomeTotal += this.getIncomeAmountByType(fromModel.INCOMETYPE.SE, role);    
    return incomeTotal;
  }
  
  /* Goals */
  public getAllGoals(): fromModel.Goal[] {
    let goals:  fromModel.Goal[]=[];
    if(this.appData && this.appData.Plan && this.appData.Plan.goals && this.appData.Plan.goals.length>0){
      goals = this.appData.Plan.goals;      
    }
    return goals;
  }

  public getAllIncludedGoals(): fromModel.Goal[] {
    let goals:  fromModel.Goal[];
    if(this.appData && this.appData.Plan && this.appData.Plan.goals && this.appData.Plan.goals.length>0){
      goals = this.appData.Plan.goals.filter(({isIncluded}) => isIncluded === true);   
    }
    return goals;
  }

  public getAllExcludedGoals(): fromModel.Goal[] {
    let goals:  fromModel.Goal[];
    if(this.appData && this.appData.Plan && this.appData.Plan.goals && this.appData.Plan.goals.length>0){
      goals = this.appData.Plan.goals.filter(({isIncluded}) => isIncluded === false);   
    }
    return goals;
  }

  public getGoalByType(goalType: fromModel.GOALTYPE): fromModel.Goal {
    let goal:  fromModel.Goal;
    if(this.appData && this.appData.Plan && this.appData.Plan.goals && this.appData.Plan.goals.length>0){
      const _goal = this.appData.Plan.goals.find(gl=> gl.goalType === goalType);
      if(_goal){
        goal = _goal;
      }      
    }
    return goal;
  } 

  public getGoalByGoalId(goalId: string): fromModel.Goal {
    let goal:  fromModel.Goal;
    if(this.appData && this.appData.Plan && this.appData.Plan.goals && this.appData.Plan.goals.length>0){
      const _goal = this.appData.Plan.goals.find(gl=> gl.goalId === goalId);
      if(_goal){
        goal = _goal;
      }      
    }
    return goal;
  }

  public getGoalByExpenseId(expenseId: string): fromModel.Goal{
    let _goal: fromModel.Goal;
    let goals: fromModel.Goal[] = this.getAllGoals();

    

    goals.forEach(goal => {
     goal.expenses.forEach(expId => {
       if(expId===expenseId){               
        _goal =  goal;        
        return _goal;
       }
     });      
    });
    return _goal;
  }
  
  public getGoalByIncomeId(incomeId: string): fromModel.Goal{
    let _goal: fromModel.Goal;
    let goals: fromModel.Goal[] = this.getAllGoals();
    goals.forEach(goal => {
     goal.incomes.forEach(incId => {
       if(incId===incomeId){               
        _goal =  goal;        
        return _goal;
       }
     });      
    });
    return _goal;
  }

  public getGoalAmountByGoalType(goalType: fromModel.GOALTYPE): number {
    let goalAmount = 0;
    let expenses = this.getAllExpensesByGoalType(goalType);
    if(expenses && expenses.length>0){
      expenses.forEach(exp => {
        if(exp.amtPeriod && exp.amtPeriod.length>0 && exp.isIncluded){
          exp.amtPeriod.forEach(amtPer => {
            goalAmount +=  Number(amtPer.amount);  
          });
        }
      });
    }
    return goalAmount;
  }

  /* Current Allocation */
  public getCurrentAllocation() : fromModel.Model {
    let currentAllocation: fromModel.Model = null;
    if(this.appData && this.appData.Plan && this.appData.Plan.currentAlloc){
      currentAllocation = this.appData.Plan.currentAlloc;
    }
    return currentAllocation;
  }

  /* Target Allocation */
  public getTargetAllocation() : fromModel.Model {
    let targetAllocation: fromModel.Model = null;
    if(this.appData && this.appData.Plan && this.appData.Plan.targetAlloc){
      targetAllocation = this.appData.Plan.targetAlloc;
    }
    return targetAllocation;
  }

  /* Settings */
  public getAllSettings(): fromModel.CodeValue[] {
    let settings:  fromModel.CodeValue[];
    if(this.appData && this.appData.Plan && this.appData.Plan.settings && this.appData.Plan.settings.length>0){
      settings = this.appData.Plan.settings;      
    }
    return settings;
  }

  public getSettingsByCode(setCode: fromModel.SETTINGSCODE): fromModel.CodeValue {
    let setting:  fromModel.CodeValue;
    if(this.appData && this.appData.Plan && this.appData.Plan.settings && this.appData.Plan.settings.length>0){
      const _set = this.appData.Plan.settings.find(set=> set.code === setCode);
      if(_set){
        setting = _set;
      }      
    }
    return setting;
  } 

  /* Plan */
  
  // public getPlanresultViewBy(): fromModel.PLANRESULTVIEWBY{
  //   let viewBy:fromModel.PLANRESULTVIEWBY = fromModel.PLANRESULTVIEWBY.PV;
  //   if(this.appData && this.appData.Plan && this.appData.Plan.planResultViewBy){
  //     viewBy = <fromModel.PLANRESULTVIEWBY>(this.appData.Plan.planResultViewBy.toUpperCase());
  //   }
  //   return viewBy;
  // }

   
  public getPlanresultViewBy(): fromModel.PLANRESULTVIEWBY{
    let viewBy:fromModel.PLANRESULTVIEWBY = fromModel.PLANRESULTVIEWBY.PV;
    let setting = this.getSettingsByCode(fromModel.SETTINGSCODE.PLAN_RESULT_VIEW_BY);
    if(setting){
      viewBy = <fromModel.PLANRESULTVIEWBY>setting.value.toUpperCase();
    } 
    return viewBy;
  }

  public setClientInContext(){  //call only in create plan workflow and add/remove spouse and other clients - called this to read plan clients from inflight plan and then get client details and then account details
    //let planClientIds = [];
    if(this.appData.Plan && this.appData.Plan.planClients && this.appData.Plan && this.appData.Plan.planClients.length>0)
    {
      // this.appData.Plan.planClients.forEach(pCl => {
      //   planClientIds.push(pCl.clientid);
      // });
      this.clientsService.getClientDetails(this.appData.Plan.planClients, this);
    }
  }

  public setPlanInContext(planId){ //call only in open plan workflow - called this to get plan details and then get client details and then account details
    this.planService.getPlanDetails(planId, this);
  }

  public savePlan(){
    this.planService.savePlan(this.appData.Plan, this);    
  }

  /* Goal Analysis */
  public getPlanAnalysis(requestParam?: fromModel.GoalAnalysisRequestParam){
    if(this.appData.Plan && (!this.appData.PlanAnalysis || this.appData.PlanAnalysis['Error'])){
      this.goalAnalysisService.getPlanAnalysis(this.appData.Plan, this, requestParam);      
    }
  }

  prepareGoalSummaryRequestParam(goalIds){
    let requestParam: fromModel.GoalAnalysisRequestParam = {
      cashFlowProjType: fromModel.CASHFLOWPROJTYPE.FULL,
      goalSmryReq: true,
      isPlanAnalytics: false,
      goalId: goalIds
    }
    return requestParam;
  }

  public getGoalSummary(){
    if(this.appData.Plan && (this.appData.GoalSummary.length===0 || this.appData.GoalSummary['Error'])){
      let goalIds = [];
      if(this.appData.Plan && this.appData.Plan.goals && this.appData.Plan.goals.length>0){
        this.appData.Plan.goals.forEach(goal => {
          goalIds.push(goal.goalId);
        });

        let requestParam = this.prepareGoalSummaryRequestParam(goalIds);
        this.goalAnalysisService.getGoalAnalysis(this.appData.Plan, this, fromModel.DATA_ACTION.GOALSUMMARY, requestParam);   
      }         
    }
  }

  
  public getGoalSummaryByGoalId(goalId: string){
    let goalSummary = null;
    if(this.appData.GoalSummary){
      goalSummary = this.appData.GoalSummary.find(_gSmry=>_gSmry.goalId === goalId);
      if(!goalSummary){
        this.getGoalSummary();        
      }
    }  
    return goalSummary;  
  }

  public getGoalSummaryArray(){
    let goalSummary = null;
    if(this.appData.GoalSummary){
      goalSummary = this.appData.GoalSummary;
    }
    return goalSummary;
  }

  public getLifeEventArray(){
    let lifeEvents = null;
    if(this.appData.LifeEvents){
      lifeEvents = this.appData.LifeEvents;
    }
    return lifeEvents;
  }

  public getGoalSummaryByGoal(goal: fromModel.Goal){
    if(this.appData.GoalSummary && this.appData.GoalSummary.length>0){
      let goalSummary = this.appData.GoalSummary.find(_gSmry=>_gSmry.goalId === goal.goalId);
      if(!goalSummary || (goalSummary && goalSummary.GoalSummary && goalSummary.GoalSummary['Error'])){
        let goalIds = [goal.goalId];
        let requestParam = this.prepareGoalSummaryRequestParam(goalIds);
        this.goalAnalysisService.getGoalAnalysis(this.appData.Plan, this, fromModel.DATA_ACTION.GOALSUMMARY, requestParam);     
      }
    }    
  }

  public getPlanAnalysisyoyCashFlows(){
    let yoyCashFlows = null;
    //if(this.appData.Plan && this.appData.Plan.planResultViewBy && this.appData.Plan.planResultViewBy === fromModel.PLANRESULTVIEWBY.FV){
    if(this.appData.Plan && this.getSettingsByCode(fromModel.SETTINGSCODE.PLAN_RESULT_VIEW_BY) && this.getSettingsByCode(fromModel.SETTINGSCODE.PLAN_RESULT_VIEW_BY).value === fromModel.PLANRESULTVIEWBY.FV){
      if(this.appData.PlanAnalysis && this.appData.PlanAnalysis["yoyCashFlows"] && this.appData.PlanAnalysis["yoyCashFlows"].length>0){
        yoyCashFlows = this.appData.PlanAnalysis["yoyCashFlows"];
      } else if(this.appData.PlanAnalysis && this.appData.PlanAnalysis["Error"]){
        yoyCashFlows = this.appData.PlanAnalysis["Error"];
      }
    } else {
      if(this.appData.PlanAnalysis && this.appData.PlanAnalysis["yoyCashFlowsPV"] && this.appData.PlanAnalysis["yoyCashFlowsPV"].length>0){
        yoyCashFlows = this.appData.PlanAnalysis["yoyCashFlowsPV"];
      } else if(this.appData.PlanAnalysis && this.appData.PlanAnalysis["Error"]){
        yoyCashFlows = this.appData.PlanAnalysis["Error"];
      }
    }
    
    return yoyCashFlows;
  }

  public getAppliedPlan(){
    let appliedPlan = null;
    if(this.appData.PlanAnalysis && this.appData.PlanAnalysis["appliedPlan"]){
      appliedPlan = this.appData.PlanAnalysis["appliedPlan"];
    } else if(this.appData.PlanAnalysis && this.appData.PlanAnalysis["Error"]){
      appliedPlan = this.appData.PlanAnalysis["Error"];
    }
    return appliedPlan;
  }

  public getPlanAnalysisProjectionOld(){
    let projection = null;
    if(this.appData.PlanAnalysis && this.appData.PlanAnalysis["projection"]
      && this.appData.PlanAnalysis["projection"].projection
      && this.appData.PlanAnalysis["projection"].projection.length>0){
      projection = this.appData.PlanAnalysis["projection"].projection;
    } else if(this.appData.PlanAnalysis && this.appData.PlanAnalysis["Error"]){
      projection = this.appData.PlanAnalysis["Error"];
    }
    return projection;
  }

  public getPlanAnalysisProjection(){
    let projection = null;
    if(this.appData.PlanAnalysis && this.appData.PlanAnalysis["projection"]){
      //if(this.appData.Plan && this.appData.Plan.planResultViewBy === fromModel.PLANRESULTVIEWBY.FV){
      if(this.appData.Plan && this.getSettingsByCode(fromModel.SETTINGSCODE.PLAN_RESULT_VIEW_BY) && this.getSettingsByCode(fromModel.SETTINGSCODE.PLAN_RESULT_VIEW_BY).value === fromModel.PLANRESULTVIEWBY.FV){
        if(this.appData.PlanAnalysis["projection"].projection && this.appData.PlanAnalysis["projection"].projection.length>0){
          projection = this.appData.PlanAnalysis["projection"].projection;
        } else if(this.appData.PlanAnalysis && this.appData.PlanAnalysis["Error"]){
          projection = this.appData.PlanAnalysis["Error"];
        }
      } else {
        if(this.appData.PlanAnalysis["projection"].projectionPV && this.appData.PlanAnalysis["projection"].projectionPV.length>0){
          projection = this.appData.PlanAnalysis["projection"].projectionPV;
        } else if(this.appData.PlanAnalysis && this.appData.PlanAnalysis["Error"]){
          projection = this.appData.PlanAnalysis["Error"];
        }
      }
    }
    return projection;
  }

  public getChartSeries(requestParam: fromModel.ChartSeriesReqParam): fromModel.ChartSeries{
    let chartSeries: fromModel.ChartSeries;
    chartSeries = fromTransformation.ChartDataTransformaion.getChartSeries(requestParam, this, this.refDataService);
    if(Constant.config.logChartSeries && chartSeries){
      this.chartDataService.dispatchAction(fromModel.CHART_DATA_ACTION.CHARTSERIES, "DataService-getChartSeries",chartSeries);
    }     
    return chartSeries;
  }

  // public getGoalSummaryByGoalId(goalId: string){
  //   let goalSummary = null;
  //   if(this.appData.GoalSummary && this.appData.GoalSummary.length>0){
  //     goalSummary = this.appData.GoalSummary.find(_gSmry=>_gSmry.goalId === goalId);
  //     if(!goalSummary || (goalSummary && goalSummary.GoalSummary && goalSummary.GoalSummary['Error'])){
  //       let goalIds = [goalId];
  //       let requestParam = this.prepareGoalSummaryRequestParam(goalIds);
  //       this.goalAnalysisService.getGoalAnalysis(this.appData.Plan, this, fromModel.DATA_ACTION.GOALSUMMARY, requestParam);     
  //     }
  //   }  
  //   return goalSummary;  
  // }

  // public getGoalSummaryArray(){
  //   let goalSummary = null;
  //   if(this.appData.GoalSummary && this.appData.GoalSummary.length>0){
  //     goalSummary = this.appData.GoalSummary;
  //   } else if(this.appData.GoalSummary && this.appData.GoalSummary["Error"]){
  //     goalSummary = this.appData.GoalSummary["Error"];
  //   }
  //   return goalSummary;
  // }

  // public getGoalSummaryArray(){
  //   let goalSummary = null;
  //   if(this.appData.GoalSummary && this.appData.GoalSummary["goalSmry"].length>0){
  //     goalSummary = this.appData.GoalSummary["goalSmry"];
  //   } else if(this.appData.GoalSummary && this.appData.GoalSummary["Error"]){
  //     goalSummary = this.appData.GoalSummary["Error"];
  //   }
  //   return goalSummary;
  // }

  // public getGoalSummary(){
  //   if(this.appData.Plan && (this.appData.GoalSummary.length===0 || this.appData.GoalSummary['Error'])){
  //     let goalIds = [];
  //     if(this.appData.Plan && this.appData.Plan.goals && this.appData.Plan.goals.length>0){
  //       this.appData.Plan.goals.forEach(goal => {
  //         goalIds[0] = goal.goalId;
  //         let requestParam = this.prepareGoalSummaryRequestParam(goalIds);
  //         this.goalAnalysisService.getGoalAnalysis(this.appData.Plan, this, fromModel.DATA_ACTION.GOALSUMMARY, requestParam);   
  //       });
  //     }         
  //   }
  // }
}




// case fromModel.DATA_ACTION.GOALSUMMARY:
      //   if(payload && payload.goalSmry && payload.goalSmry.length>0){

      //     payload.goalSmry.forEach(gsam => {

      //       let goalSummaryPos;
      //       let goalSummary = this.appData.GoalSummary.find(_gSmry=>_gSmry.goalId === gsam.goalId);
  
      //       if(goalSummary){
      //         goalSummaryPos = this.appData.GoalSummary.indexOf(goalSummary);
      //       }
      //       if(goalSummaryPos){
      //         this.appData.GoalSummary[goalSummaryPos] = {goalId: gsam.goalId, GoalSummary: gsam};
      //       }
      //       else {
      //         this.appData.GoalSummary.push({goalId: gsam.goalId, GoalSummary: gsam});
      //       } 
            
      //     });
                  
      //   }        
      //   break;  
        // case fromModel.DATA_ACTION.GOALSUMMARY:
      //   if(payload && payload.goalSmry && payload.goalSmry.length>0){
      //     let goalSummaryPos;
      //     let goalSummary = this.appData.GoalSummary.find(_gSmry=>_gSmry.goalId === payload.goalSmry.goalId);

      //     if(goalSummary){
      //       goalSummaryPos = this.appData.GoalSummary.indexOf(goalSummary);
      //     }
      //     if(goalSummaryPos){
      //       this.appData.GoalSummary[goalSummaryPos] = {goalId: payload.goalSmry[0].goalId, GoalSummary: payload};
      //     }
      //     else {
      //       this.appData.GoalSummary.push({goalId: payload.goalSmry[0].goalId, GoalSummary: payload});
      //     }          
      //   }        
      //   break;


      // public setPlanInContext(planId){
  //   this.planService.getPlanDetails(planId, this);
  // }

  // public setClientInContext(){

  //   if(this.appData.Plan && this.appData.Plan.planClients && this.appData.Plan && this.appData.Plan.planClients.length>0)
  //   {
  //     let allAccountIds = [];
  //     let planClientIds = [];

  //     this.appData.Plan.planClients.forEach(pCl => {
  //       planClientIds.push(pCl.clientid);
  //     });

  //     this.clientsService.getClientAPI(planClientIds).subscribe(clients=>{
  //       clients['value'].forEach(cl => {
  //         cl.Account.forEach(acc => {
  //           allAccountIds.push(acc);  
  //         });              
  //         cl.externalAccounts.forEach(extAcc => {
  //           allAccountIds.push(extAcc);  
  //         });
  //       });
  //       //this.contextService.dispatchAction(fromModel.APP_ACTION.ACCOUNT, "PlanService-getPlanDetails",allAccountIds);                   
  //       this.dispatchAction(fromModel.DATA_ACTION.CLIENTS,"DataService-setClientInContext", clients);
  //       this.accountsService.getAccounts(allAccountIds, this);
  //     }, err=>{  
  //       this.dispatchAction(fromModel.DATA_ACTION.CLIENTS,"DataService-setClientInContext-ClientDetails", {Error: fromModel.ERRORCODE.APIERROR, ErrorDetails: JSON.stringify(err)});
  //       console.log("Error getting Client Details: " + JSON.stringify(err));      
  //       //this.appMonitoringService.traceMsg("Error getting Client Details: ", JSON.stringify(err) , 'Error');            
  //     });
  //   }
  // }